/* DOT API Client Configuration */

import { OpenAPI } from './generated/core/OpenAPI';
import { setupApiInterceptors } from './interceptors';
import { getCurrentAsOf } from './asOfContext';
import type { Store } from '@reduxjs/toolkit';

// API Configuration
export interface ApiConfig {
  baseUrl?: string;
  token?: string;
  credentials?: 'include' | 'omit' | 'same-origin';
}

// Default configuration
// VITE_API_BASE_URL 이 비어 있으면 상대 경로(= 페이지와 같은 오리진)로 요청한다.
// 모든 배포 환경에서 API 는 프론트와 같은 오리진에서 서빙되므로 이것이 기본값이며,
// 덕분에 환경별로 다시 빌드하지 않고 하나의 번들을 공유할 수 있다.
const defaultConfig: ApiConfig = {
  baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
  credentials: 'include'
};

/**
 * Configure the API client
 */
export function configureApi(config: Partial<ApiConfig> = {}) {
  const finalConfig = { ...defaultConfig, ...config };

  // Configure the generated OpenAPI client
  OpenAPI.BASE = finalConfig.baseUrl!;
  if (finalConfig.credentials) {
    OpenAPI.CREDENTIALS = finalConfig.credentials;
  }

  if (finalConfig.token) {
    OpenAPI.TOKEN = finalConfig.token;
  }

  // 모든 API 요청에 동적 헤더 추가 (매 요청마다 localStorage에서 최신 값 조회)
  OpenAPI.HEADERS = async () => {
    const headers: Record<string, string> = {
      'DOT-CLIENT-TYPE': 'dot-frontend'
    };

    // dot_current_org_member에서 DOT-MEMBER-ID 추출
    try {
      const orgMemberStr = localStorage.getItem('dot_current_org_member');
      if (orgMemberStr) {
        const orgMember = JSON.parse(orgMemberStr);
        if (orgMember?.id) {
          headers['DOT-MEMBER-ID'] = orgMember.id;
        }
      }
    } catch {
      // JSON 파싱 실패 시 무시
    }

    // dot_selected_workspace에서 DOT-TARGET-ORG-ID, DOT-TARGET-WORKSPACE-ID 추출
    try {
      const workspaceStr = localStorage.getItem('dot_selected_workspace');
      if (workspaceStr) {
        const workspace = JSON.parse(workspaceStr);
        if (workspace?.org_id) {
          headers['DOT-TARGET-ORG-ID'] = workspace.org_id;
        }
        if (workspace?.id) {
          headers['DOT-TARGET-WORKSPACE-ID'] = workspace.id;
        }
      }
    } catch {
      // JSON 파싱 실패 시 무시
    }

    // 스냅샷 조회 컨텍스트 (현재 화면이 as_of를 지정한 경우에만 전송)
    const asOf = getCurrentAsOf();
    if (asOf) {
      headers['X-As-Of'] = asOf;
    }

    return headers;
  };

  return finalConfig;
}

/**
 * Set authentication token
 */
export function setAuthToken(token: string | null) {
  if (token) {
    OpenAPI.TOKEN = token;
  } else {
    delete OpenAPI.TOKEN;
  }
}

/**
 * Get current API configuration
 */
export function getApiConfig() {
  return {
    baseUrl: OpenAPI.BASE,
    token: OpenAPI.TOKEN,
    credentials: OpenAPI.CREDENTIALS
  };
}

/**
 * Initialize API client with default configuration
 */
export function initializeApi(store?: Store) {
  const config = configureApi();
  
  // Setup interceptors if store is provided
  if (store) {
    setupApiInterceptors(store);
  }
  
  return config;
}

// Export interceptor utilities
export { setupApiInterceptors, refreshTokenIfNeeded } from './interceptors';

// Export OpenAPI for direct access if needed
export { OpenAPI } from './generated/core/OpenAPI';
export { ApiError } from './generated/core/ApiError';
export type { CancelablePromise } from './generated/core/CancelablePromise';